import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SectionHeader } from "@/components/marketing/Sections";

const FORM_TYPES = [
    "consultation", "property_submission", "investor_inquiry", "sub_vendor_registration",
    "contact", "career_inquiry", "demo_request",
];

const STATUSES = ["new", "reviewed", "converted", "archived"];

export default function LeadInbox() {
    const [rows, setRows] = useState([]);
    const [formType, setFormType] = useState("");
    const [status, setStatus] = useState("");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        const q = new URLSearchParams();
        if (formType) q.set("form_type", formType);
        if (status) q.set("status", status);
        const { data } = await api.get(`/admin/leads?${q.toString()}`);
        setRows(data);
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [formType, status]);

    const changeStatus = async (id, s) => {
        await api.patch(`/admin/leads/${id}`, { status: s });
        toast.success("Updated");
        load();
        if (selected?.id === id) setSelected({ ...selected, status: s });
    };

    return (
        <div className="section" data-testid="lead-inbox">
            <div className="container-editorial">
                <SectionHeader overline="Super Admin" title="Lead Inbox" sub="All public form submissions across the marketing sites." />

                <div className="mt-8 flex flex-wrap gap-3 items-center">
                    <select value={formType} onChange={(e) => setFormType(e.target.value)} className="border border-border bg-background px-3 py-2 text-sm" data-testid="filter-form-type">
                        <option value="">All types</option>
                        {FORM_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-border bg-background px-3 py-2 text-sm" data-testid="filter-status">
                        <option value="">All statuses</option>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={load} className="border border-border px-3 py-2 text-sm hover:bg-secondary" data-testid="refresh-leads">Refresh</button>
                </div>

                <div className="mt-6 border border-border" data-testid="lead-inbox-table">
                    <div className="grid grid-cols-6 gap-3 px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-muted-foreground">
                        <div className="col-span-2">Contact</div>
                        <div>Type</div>
                        <div>Status</div>
                        <div>When</div>
                        <div className="text-right">Actions</div>
                    </div>
                    {loading && <div className="p-8 text-sm text-muted-foreground">Loading…</div>}
                    {!loading && rows.length === 0 && <div className="p-8 text-sm text-muted-foreground">No leads yet.</div>}
                    {rows.map((r) => {
                        const p = r.payload || {};
                        const who = [p.first_name || p.your_name || p.contact_name, p.last_name].filter(Boolean).join(" ") || p.business_name || p.email || "—";
                        return (
                            <div key={r.id} className="grid grid-cols-6 gap-3 px-4 py-4 border-b border-border last:border-b-0 items-center text-sm" data-testid={`lead-row-${r.id}`}>
                                <div className="col-span-2">
                                    <div className="font-medium">{who}</div>
                                    <div className="text-xs text-muted-foreground">{p.email || p.phone || ""}</div>
                                </div>
                                <div className="text-xs">{r.form_type}</div>
                                <div>
                                    <select
                                        value={r.status}
                                        onChange={(e) => changeStatus(r.id, e.target.value)}
                                        className="border border-border bg-background px-2 py-1 text-xs"
                                        data-testid={`lead-status-${r.id}`}
                                    >
                                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</div>
                                <div className="text-right">
                                    <button onClick={() => setSelected(r)} className="text-xs underline" data-testid={`lead-view-${r.id}`}>View</button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {selected && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-6" onClick={() => setSelected(null)}>
                        <div className="bg-background border border-border max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="lead-detail-panel">
                            <div className="p-6 border-b border-border">
                                <div className="overline">{selected.form_type}</div>
                                <div className="mt-2 font-display text-2xl tracking-tight">Lead details</div>
                                <div className="text-xs text-muted-foreground mt-1">{new Date(selected.created_at).toLocaleString()}</div>
                            </div>
                            <div className="p-6 space-y-3">
                                {Object.entries(selected.payload || {}).map(([k, v]) => (
                                    <div key={k} className="grid grid-cols-3 gap-4 border-b border-border pb-2">
                                        <div className="text-xs uppercase tracking-widest text-muted-foreground">{k.replace(/_/g, " ")}</div>
                                        <div className="col-span-2 text-sm">{String(v || "—")}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="p-6 border-t border-border flex justify-end">
                                <button onClick={() => setSelected(null)} className="border border-border px-4 py-2 text-sm">Close</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
